import { useState } from 'react';
import { useDidMount } from '@djs/react';
import { LazyComponentFallbackProps, LazyComponentFallbackSeparateProps, LazyComponentProps } from './types';

/**
 * Вспомогательный компонент для ленивой подгрузки
 */
export function LazyComponent<T, G, K, F>(props: LazyComponentProps<T, G, K, F>) {
  const { forceSkeleton, importCallback, importCallbackSelector, ErrorFallback, LoadingFallback, ...componentProps } =
    props as LazyComponentProps<T, G, K, F> & LazyComponentFallbackProps & LazyComponentFallbackSeparateProps<K, F>;

  const [{ error, Component }, setState] = useState(() => {
    if (importCallback.cache) {
      return { error: false, Component: importCallbackSelector(importCallback.cache.result) };
    }

    return { error: false, Component: null };
  });

  const loadData = () => {
    importCallback()
      .then((args) => {
        setState({ error: false, Component: importCallbackSelector(args) });
      })
      .catch(() => {
        setState({ error: true, Component: null });
      });
  };

  useDidMount(() => {
    if (!Component) {
      loadData();
    }
  });

  if (Component && !forceSkeleton) {
    return <Component {...(componentProps as any as T & React.JSX.IntrinsicAttributes)} />;
  }

  if ('fallback' in props) {
    return <props.fallback error={error} retry={loadData} />;
  }

  if (error) {
    return <ErrorFallback {...(props as any)} retry={loadData} />;
  }

  return <LoadingFallback {...props} />;
}
